import { ChangeDetectionStrategy, Component, computed, input, OnDestroy, signal } from '@angular/core';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatTooltipModule } from '@angular/material/tooltip';
import { TranslocoModule } from '@jsverse/transloco';

import { CapitalizePipe } from '@hmax-order/frontend/utils';
import { StorageFile } from '../../../interfaces';

interface PendingFile {
  file: File;
  previewUrl: string;
}

const ALLOWED_TYPES = ['image/jpeg', 'image/png', 'image/webp'];
const ALLOWED_EXTENSIONS = '.jpg,.jpeg,.png,.webp';

@Component({
  selector: 'hmax-input-upload-file',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [MatIconModule, MatProgressSpinnerModule, MatTooltipModule, TranslocoModule, CapitalizePipe],
  templateUrl: './input-upload-file.component.html',
  styleUrl: './input-upload-file.component.scss',
})
export class InputUploadFileComponent implements OnDestroy {
  images = input<StorageFile[]>([]);
  isLoading = input<boolean>(false);
  maxImages = input<number | null>(null);
  /** `'square'` preserva o comportamento atual (logo em Configurações). `'landscape'` é 4/3, usado no cardápio. */
  aspectRatio = input<'square' | 'landscape'>('square');

  pendingFiles = signal<PendingFile[]>([]);
  pendingDeletions = signal<StorageFile[]>([]);
  isDragging = signal(false);
  allowedExtensions = ALLOWED_EXTENSIONS;

  gridClass = computed<string>(() => {
    const max = this.maxImages();
    if (max === 1) return 'grid grid-cols-1 gap-2';
    return 'grid grid-cols-3 gap-2 sm:grid-cols-4';
  });

  aspectClass = computed<string>(() => (this.aspectRatio() === 'landscape' ? 'aspect-[4/3]' : 'aspect-square'));

  canAddMore = computed<boolean>(() => {
    const max = this.maxImages();
    if (max === null) return true;
    return this.allImages().length < max;
  });

  allImages = computed<StorageFile[]>(() => {
    const deletionUrls = new Set(this.pendingDeletions().map((d) => d.url));
    return [
      ...this.images().filter((img) => !deletionUrls.has(img.url)),
      ...this.pendingFiles().map((p) => ({ url: p.previewUrl, name: p.file.name })),
    ];
  });

  ngOnDestroy(): void {
    this.pendingFiles().forEach((p) => URL.revokeObjectURL(p.previewUrl));
  }

  onFileSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    const file = input.files?.[0];

    if (!file) return;

    if (!ALLOWED_TYPES.includes(file.type)) {
      input.value = '';
      return;
    }

    if (!this.canAddMore()) {
      input.value = '';
      return;
    }

    this._addPendingFile(file);
    input.value = '';
  }

  onDragOver(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
    this.isDragging.set(true);
  }

  onDragLeave(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
    this.isDragging.set(false);
  }

  onDrop(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
    this.isDragging.set(false);

    const file = event.dataTransfer?.files?.[0];
    if (!file || !ALLOWED_TYPES.includes(file.type) || !this.canAddMore()) return;

    this._addPendingFile(file);
  }

  deleteImage(image: StorageFile): void {
    const pendingFile = this.pendingFiles().find((p) => p.previewUrl === image.url);

    if (pendingFile) {
      URL.revokeObjectURL(pendingFile.previewUrl);
      this.pendingFiles.update((current) => current.filter((p) => p.previewUrl !== image.url));
      return;
    }

    this.pendingDeletions.update((current) => [...current, image]);
  }

  hasPendingFiles(): boolean {
    return this.pendingFiles().length > 0;
  }

  hasPendingChanges(): boolean {
    return this.pendingFiles().length > 0 || this.pendingDeletions().length > 0;
  }

  getPendingFiles(): File[] {
    return this.pendingFiles().map((p) => p.file);
  }

  getPendingDeletions(): StorageFile[] {
    return this.pendingDeletions();
  }

  clearAfterUpload(): void {
    this.pendingFiles().forEach((p) => URL.revokeObjectURL(p.previewUrl));
    this.pendingFiles.set([]);
  }

  clearAfterDeletion(deletedUrls: string[]): void {
    const urlSet = new Set(deletedUrls);
    this.pendingDeletions.update((current) => current.filter((d) => !urlSet.has(d.url)));
  }

  reset(): void {
    this.pendingFiles().forEach((p) => URL.revokeObjectURL(p.previewUrl));
    this.pendingFiles.set([]);
    this.pendingDeletions.set([]);
  }

  private _addPendingFile(file: File): void {
    const previewUrl = URL.createObjectURL(file);
    this.pendingFiles.update((current) => [...current, { file, previewUrl }]);
  }
}
